import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useGame } from '../../contexts/GameContext';
import multiplayerService from '../../services/multiplayerService';
import { joinRoom, leaveRoom } from '../../services/websocketService';
import GameRound from '../game/GameRound';
import SplashScreen from '../game/SplashScreen';
import MultiplayerResults from './MultiplayerResults';
import { MapPin, Medal } from 'lucide-react';
import { avatarSrc } from '../avatar/avatarList';
import './MultiplayerGameContainer.css';

const MultiplayerGameContainer = ({ room, initialRound, onGameEnd }) => {
  const { user } = useAuth();
  const { setIsPlaying } = useGame();
  const [gameState, setGameState] = useState('playing'); // playing, round_result, completed
  const [currentRound, setCurrentRound] = useState(initialRound?.round_number || 1);
  const [userGuess, setUserGuess] = useState(null); // Store user's guess for result display
  
  // Parse hints from initialRound if needed
  const parseInitialRound = (round) => {
    if (!round) return null;
    let hints = round.hints || [];
    if (typeof hints === 'string') {
      try {
        hints = JSON.parse(hints);
      } catch (e) {
        console.error('Failed to parse hints from string:', e);
        hints = [];
      }
    }
    const parsedRound = {
      ...round,
      hints: Array.isArray(hints) ? hints : []
    };
    console.log('Parsed initial round - hints:', parsedRound.hints);
    return parsedRound;
  };
  
  const [showSplash, setShowSplash] = useState((initialRound?.round_number ?? 1) === 1);
  const [roundData, setRoundData] = useState(parseInitialRound(initialRound));
  
  // Debug: Log roundData when it changes
  useEffect(() => {
    if (roundData) {
      console.log('RoundData updated:', {
        round_number: roundData.round_number,
        hints: roundData.hints,
        hintsType: typeof roundData.hints,
        hintsIsArray: Array.isArray(roundData.hints),
        hintsLength: Array.isArray(roundData.hints) ? roundData.hints.length : 'N/A'
      });
    }
  }, [roundData]);
  const [roundResult, setRoundResult] = useState(null);
  const [allResults, setAllResults] = useState(null);
  const [participants, setParticipants] = useState(room.participants || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(initialRound?.remaining_time ?? room.round_time_limit);
  const [toasts, setToasts] = useState([]);
  
  const timerRef = useRef(null);
  // Wall-clock instant (ms) the round ends. Driving the countdown from a fixed
  // deadline (anchored to the server's remaining-time duration) keeps every
  // client in sync and immune to setInterval drift across refreshes.
  const deadlineRef = useRef(Date.now() + (initialRound?.remaining_time ?? room.round_time_limit) * 1000);
  const isHost = room.host?.id === user?.id;

  const handleSplashComplete = useCallback(() => setShowSplash(false), []);

  // Update game playing state for hiding nav/footer
  // Always hide footer when this component is mounted (during entire multiplayer game)
  useEffect(() => {
    setIsPlaying(true);
    return () => setIsPlaying(false); // Cleanup on unmount
  }, [setIsPlaying]);

  // Add toast notification
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // Handle WebSocket events
  const handlePlayerGuessed = useCallback((data) => {
    if (data.player_id !== user?.id) {
      addToast(`${data.player_name} has made their guess!`, 'guess');
    }
  }, [user?.id, addToast]);

  const handleRoundCompleted = useCallback((data) => {
    setRoundResult(data.results);
    setGameState('round_result');
    
    // Update participant scores
    if (data.results?.guesses) {
      setParticipants(prev => {
        const updated = [...prev];
        data.results.guesses.forEach(guess => {
          const participant = updated.find(p => p.id === guess.user_id);
          if (participant) {
            participant.total_score = (participant.total_score || 0) + guess.score;
          }
        });
        return updated.sort((a, b) => b.total_score - a.total_score);
      });
    }
  }, []);

  const handleRoundStarted = useCallback((data) => {
    // Parse hints if they're a string or ensure they're an array
    let parsedHints = data.hints || [];
    
    // If hints is a string, try to parse it
    if (typeof parsedHints === 'string') {
      try {
        parsedHints = JSON.parse(parsedHints);
      } catch (e) {
        console.error('Failed to parse hints from string:', e);
        parsedHints = [];
      }
    }
    
    // Ensure hints is an array
    if (!Array.isArray(parsedHints)) {
      console.warn('Hints is not an array:', parsedHints, typeof parsedHints);
      parsedHints = [];
    }
    
    const roundDataWithHints = {
      ...data,
      hints: parsedHints
    };
    
    console.log('Round started - data:', data);
    console.log('Round started - parsed hints:', parsedHints);
    console.log('Round started - final roundData:', roundDataWithHints);
    
    setRoundData(roundDataWithHints);
    setCurrentRound(data.round_number);
    setRoundResult(null);
    setHasGuessed(false);
    setUserGuess(null); // Reset user guess for new round
    deadlineRef.current = Date.now() + data.time_limit * 1000;
    setTimeLeft(data.time_limit);
    setGameState('playing');
  }, []);

  const handleGameCompleted = useCallback((data) => {
    setAllResults(data.results);
    setGameState('completed');
  }, []);

  // Set up WebSocket listeners
  useEffect(() => {
    joinRoom(room.room_code, {
      onPlayerGuessed: handlePlayerGuessed,
      onRoundCompleted: handleRoundCompleted,
      onRoundStarted: handleRoundStarted,
      onGameCompleted: handleGameCompleted,
    });

    return () => {
      leaveRoom(room.room_code);
    };
  }, [room.room_code, handlePlayerGuessed, handleRoundCompleted, handleRoundStarted, handleGameCompleted]);

  // Handle timeout guess
  const handleTimeoutGuess = useCallback(async () => {
    if (hasGuessed) return;
    
    setHasGuessed(true);
    setIsSubmitting(true);
    
    try {
      const response = await multiplayerService.submitGuess(room.room_code, currentRound, {
        latitude: null,
        longitude: null,
        timeTaken: Math.floor(room.round_time_limit), // Ensure integer
      });
      
      if (response.success) {
        addToast('Time ran out! Guess submitted.', 'info');
      }
    } catch (err) {
      console.error('Timeout guess error:', err);
      console.error('Error details:', err.response?.data);
      // Don't show error toast for timeout - it's expected
    } finally {
      setIsSubmitting(false);
    }
  }, [hasGuessed, room.room_code, currentRound, room.round_time_limit, addToast]);

  // Handle hint revealed (deduct 5 seconds)
  const handleHintRevealed = useCallback(() => {
    deadlineRef.current -= 5000;
    setTimeLeft(prev => Math.max(0, prev - 5));
    addToast('Hint revealed! -5 seconds', 'info');
  }, [addToast]);

  // Timer countdown — derived from the fixed deadline each tick so it never
  // drifts and stays consistent across clients and refreshes.
  useEffect(() => {
    if (gameState === 'playing' && !hasGuessed) {
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          // Auto-submit empty guess on timeout
          handleTimeoutGuess();
        }
      };
      tick(); // sync immediately on mount/round change
      timerRef.current = setInterval(tick, 500);

      return () => clearInterval(timerRef.current);
    }
  }, [gameState, hasGuessed, handleTimeoutGuess]);

  // Server-authoritative round end. Once the round clock runs out, ANY active
  // client (whether it guessed or not) asks the server to end the round. The
  // server re-checks the real deadline, so this can never end a round early.
  // This is what stops a round hanging on "Waiting for other players..." when a
  // player disconnected or their tab was throttled and never self-reported its
  // timeout — a single foreground client is enough to resolve it for everyone.
  useEffect(() => {
    if (gameState !== 'playing') return;
    // +1.5s grace so any straggler real/timeout guesses land (and get scored)
    // before the server backfills the rest as timeouts.
    const fireIn = Math.max(0, deadlineRef.current - Date.now()) + 1500;
    const enforceTimer = setTimeout(() => {
      multiplayerService.forceEndRound(room.room_code, currentRound).catch(() => {});
    }, fireIn);
    return () => clearTimeout(enforceTimer);
  }, [gameState, currentRound, room.room_code]);

  // Handle guess submission
  const handleGuess = async (lat, lng) => {
    if (hasGuessed || isSubmitting) return;
    
    setIsSubmitting(true);
    setHasGuessed(true);
    clearInterval(timerRef.current);
    
    const timeTaken = Math.max(0, Math.floor(room.round_time_limit - timeLeft));
    
    // Validate coordinates
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      console.error('Invalid coordinates:', { lat, lng });
      addToast('Invalid coordinates. Please try again.', 'error');
      setHasGuessed(false);
      setIsSubmitting(false);
      return;
    }
    
    // Store the user's guess for result display
    setUserGuess({ latitude: lat, longitude: lng });
    
    try {
      const response = await multiplayerService.submitGuess(room.room_code, currentRound, {
        latitude: lat,
        longitude: lng,
        timeTaken,
      });
      
      if (response.success) {
        addToast('Guess submitted! Waiting for others...', 'success');
      }
    } catch (err) {
      console.error('Guess error:', err);
      console.error('Error details:', err.response?.data);
      addToast(err.response?.data?.message || 'Failed to submit guess', 'error');
      setHasGuessed(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle continue to next round (host only)
  const handleContinue = async () => {
    if (!isHost) return;
    
    if (currentRound >= room.total_rounds) {
      // Game complete - fetch results
      try {
        const response = await multiplayerService.getResults(room.room_code);
        if (response.success) {
          setAllResults(response.data);
          setGameState('completed');
        }
      } catch (err) {
        console.error('Get results error:', err);
      }
    } else {
      // Start next round
      try {
        const response = await multiplayerService.nextRound(room.room_code);
        if (response.success) {
          // Round started event will handle state update
        }
      } catch (err) {
        console.error('Next round error:', err);
        addToast('Failed to start next round', 'error');
      }
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  // Parse hints for GameRound component
  const parsedHints = useMemo(() => {
    if (!roundData || !roundData.hints) {
      console.log('No roundData or hints in parsedHints');
      return [];
    }
    
    let hints = roundData.hints;
    
    // If hints is a string, parse it
    if (typeof hints === 'string') {
      try {
        hints = JSON.parse(hints);
      } catch (e) {
        console.error('Failed to parse hints:', e);
        return [];
      }
    }
    
    // Ensure it's an array
    if (!Array.isArray(hints)) {
      console.warn('Hints is not an array:', hints, 'type:', typeof hints);
      return [];
    }
    
    console.log('Parsed hints for GameRound:', hints, 'length:', hints.length);
    return hints;
  }, [roundData]);

  // Format result for GameRound component (to show map with markers like single player)
  const formattedResultForGameRound = useMemo(() => {
    if (gameState !== 'round_result' || !roundResult) return null;
    
    // Find current user's guess from the results
    const currentUserGuess = roundResult.guesses?.find(g => g.user_id === user?.id);
    
    if (!currentUserGuess && !userGuess) return null;
    
    // Get actual location from round result or round data
    const actualLocation = roundResult.location || roundResult.actual_location;
    
    return {
      guess_location: {
        latitude: currentUserGuess?.guess_latitude || userGuess?.latitude,
        longitude: currentUserGuess?.guess_longitude || userGuess?.longitude,
      },
      actual_location: {
        latitude: actualLocation?.latitude,
        longitude: actualLocation?.longitude,
      },
      score: currentUserGuess?.score || 0,
      distance_km: currentUserGuess?.distance_km || 0,
    };
  }, [gameState, roundResult, user?.id, userGuess]);

  // Every player's guess, marked with their avatar, for the result map drops.
  const resultGuessesForMap = useMemo(() => {
    if (gameState !== 'round_result' || !roundResult?.guesses) return null;
    return roundResult.guesses.map((g) => ({
      ...g,
      is_current_user: g.user_id === user?.id,
    }));
  }, [gameState, roundResult, user?.id]);

  // Render game completed state
  if (gameState === 'completed' && allResults) {
    return (
      <MultiplayerResults 
        results={allResults} 
        onPlayAgain={() => onGameEnd()} 
      />
    );
  }

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <div className="multiplayer-game-container">
      {/* Timer Overlay */}
      {gameState === 'playing' && (
        <div className={`timer-overlay ${timeLeft <= 10 ? 'warning' : ''}`}>
          <span className="timer-icon">⏱️</span>
          <span className="timer-value">{formatTime(timeLeft)}</span>
        </div>
      )}

      {/* Participants Sidebar */}
      <div className="participants-sidebar">
        <div className="sidebar-header">
          <span>Players</span>
          <span className="round-info">Round {currentRound}/{room.total_rounds}</span>
        </div>
        <ul className="sidebar-participants">
          {participants.map((p, index) => (
            <li key={p.id} className={`sidebar-participant ${p.id === user?.id ? 'current-user' : ''}`}>
              <span className="rank">#{index + 1}</span>
              <span className="name">{p.name}</span>
              <span className="score">{p.total_score || 0}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>

      {/* Waiting for others overlay */}
      {hasGuessed && gameState === 'playing' && (
        <div className="waiting-overlay">
          <div className="waiting-content">
            <div className="spinner"></div>
            <p>Waiting for other players...</p>
          </div>
        </div>
      )}

      {/* Round Result Leaderboard Sidebar */}
      {gameState === 'round_result' && roundResult && (
        <div className="round-result-sidebar">
          <div className="round-result-sidebar-card">
            {/* Header */}
            <div className="sidebar-result-header">
              <span className="round-label">Round {currentRound} Results</span>
              <div className="round-location-badge">
                <MapPin size={13} className="location-icon" />
                <span className="location-name">{roundResult.location?.name}</span>
              </div>
              {roundResult.location?.description && (
                <p className="round-location-desc">{roundResult.location.description}</p>
              )}
              {roundResult.location?.facts?.length > 0 && (
                <ul className="round-location-facts">
                  {roundResult.location.facts.map((fact, i) => (
                    <li key={i}>{fact}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Leaderboard */}
            <div className="sidebar-leaderboard">
              {roundResult.guesses?.map((guess, index) => (
                <div 
                  key={guess.user_id} 
                  className={`sidebar-leaderboard-item ${guess.user_id === user?.id ? 'current-user' : ''} ${index === 0 ? 'rank-1' : ''}`}
                >
                  <div className="sidebar-rank">
                    {index === 0 && <Medal size={14} className="medal medal-1" />}
                    {index === 1 && <Medal size={14} className="medal medal-2" />}
                    {index === 2 && <Medal size={14} className="medal medal-3" />}
                    {index > 2 && <span className="rank-num">#{index + 1}</span>}
                  </div>
                  <div className="sidebar-avatar">
                    {guess.avatar
                      ? <img src={avatarSrc(guess.avatar)} alt="" />
                      : (guess.user_name?.charAt(0).toUpperCase() || '?')}
                  </div>
                  <div className="sidebar-player-info">
                    <span className="sidebar-player-name">{guess.user_name}</span>
                    {guess.user_id === user?.id && <span className="you-tag">You</span>}
                  </div>
                  <div className="sidebar-score-info">
                    {guess.is_timeout ? (
                      <span className="timeout-tag">Timeout</span>
                    ) : (
                      <>
                        <span className="sidebar-score">{guess.score?.toLocaleString()}</span>
                        <span className="sidebar-distance">{guess.distance_km?.toFixed(1)} km</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action Button */}
            <div className="sidebar-result-action">
              <button 
                className={`sidebar-continue-btn ${!isHost ? 'disabled' : ''}`} 
                onClick={isHost ? handleContinue : undefined}
                disabled={!isHost}
              >
                {currentRound >= room.total_rounds ? 'VIEW RESULTS →' : 'NEXT ROUND →'}
              </button>
              {!isHost && <span className="host-waiting-note">Waiting for host...</span>}
            </div>
          </div>
        </div>
      )}

      {/* Game Round Component - shown during playing AND round_result */}
      {(gameState === 'playing' || gameState === 'round_result') && roundData && (
        <GameRound
          roundNumber={currentRound}
          totalRounds={room.total_rounds}
          dropLocation={roundData.drop_location}
          actualLocation={null}
          hints={parsedHints}
          onGuess={handleGuess}
          isSubmitting={isSubmitting}
          result={formattedResultForGameRound}
          resultGuesses={resultGuessesForMap}
          onContinue={isHost ? handleContinue : null}
          onHintRevealed={handleHintRevealed}
        />
      )}
    </div>
  );
};

export default MultiplayerGameContainer;
